import { useScrollContext } from "@pathwright/ui/src/components/scroll/ScrollView"
import { useEffect } from "react"

const DEFAULT_HIGHLIGHT_CLASS = "smooth-scroll-active"
const DEFAULT_STYLE_ID = "smooth-scroll-temp-style"

export const injectTemporaryStyles = (
  styleId: string = DEFAULT_STYLE_ID,
  highlightClass: string = DEFAULT_HIGHLIGHT_CLASS
) => {
  const existingStyle = document.getElementById(styleId)

  if (existingStyle) {
    return () => {}
  }

  const style = document.createElement("style")
  style.id = styleId
  style.innerHTML = `
    .${highlightClass}::after {
      content: "";
      position: absolute;
      opacity: 0.5;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      animation: highlight 2.5s ease-out;
    }
  `
  document.head.appendChild(style)

  return () => {
    document.head.removeChild(style)
  }
}

const highlightElement = (
  element: HTMLElement,
  highlightClass: string = DEFAULT_HIGHLIGHT_CLASS
) => {
  element.classList.add(highlightClass)

  const handleBlur = () => {
    element.classList.remove(highlightClass)
  }

  element.addEventListener("blur", handleBlur)

  return () => {
    element.removeEventListener("blur", handleBlur)
  }
}

const scrollSmoothlyToElement = ({
  element,
  duration,
  scrollTo,
  scrollContainer,
  callback,
  offset = 0
}: {
  element: HTMLElement
  duration: number
  scrollTo: (x: number, y: number) => void
  scrollContainer?: HTMLElement | null
  callback?: () => void
  offset?: number
}) => {
  const targetPosition = scrollContainer
    ? scrollContainer.scrollTop +
      (element.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top) -
      offset
    : element.getBoundingClientRect().top + window.pageYOffset - offset
  const startPosition = scrollContainer
    ? scrollContainer.scrollTop
    : window.pageYOffset
  const distance = targetPosition - startPosition
  const startTime = performance.now()

  const animation = (currentTime: number) => {
    const elapsedTime = currentTime - startTime
    const progress = Math.min(elapsedTime / duration, 1)
    const ease =
      progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress

    scrollTo(0, startPosition + distance * ease)

    if (progress < 1) {
      requestAnimationFrame(animation)
    } else if (callback) {
      callback()
    }
  }

  requestAnimationFrame(animation)
}

export const scrollToAndHighlight = ({
  elementID,
  scrollTo,
  scrollContainer,
  duration,
  offset,
  autofocus,
  highlightClass = DEFAULT_HIGHLIGHT_CLASS
}: {
  elementID: string
  scrollTo?: (x: number, y: number) => void
  /** When set, scroll this element instead of the window. */
  scrollContainer?: HTMLElement | null
  duration: number
  offset?: number
  autofocus?: boolean
  highlightClass?: string
}) => {
  const element = document.getElementById(elementID)

  if (!element) return

  const resolvedScrollTo =
    scrollTo ||
    (scrollContainer
      ? (_x: number, y: number) => {
          scrollContainer.scrollTop = y
        }
      : undefined)

  if (!resolvedScrollTo) return

  const cleanup = highlightElement(element, highlightClass)

  scrollSmoothlyToElement({
    element,
    duration,
    scrollTo: resolvedScrollTo,
    scrollContainer,
    callback: () => {
      if (autofocus) {
        element.setAttribute("tabindex", "-1")
        element.focus()
      }
      cleanup()
    },
    offset
  })
}

type UseScrollToAndHighlightProps = {
  onScrollStart?: () => void
  onScrollEnd?: () => void
  isActive?: boolean
  scrollDuration?: number
  offset?: number
  autofocus?: boolean
  /** Resolve hash id to the actual DOM element id (e.g. map legacy block id to canonical block id) */
  resolveElementId?: (hashId: string) => string | null | undefined
  /** Class added to element for highlight (must match style). Default "smooth-scroll-active". */
  highlightClass?: string
  /** ID of the injected <style> element. Default "smooth-scroll-temp-style". */
  styleId?: string
}

const useScrollToAndHighlight = ({
  onScrollStart = () => {},
  onScrollEnd = () => {},
  isActive = true,
  scrollDuration = 500,
  offset = 0,
  autofocus = true,
  resolveElementId,
  highlightClass = DEFAULT_HIGHLIGHT_CLASS,
  styleId = DEFAULT_STYLE_ID
}: UseScrollToAndHighlightProps = {}) => {
  const scrollContext = useScrollContext() || {}
  const { scrollNode } = scrollContext

  useEffect(() => {
    const removeStyles = injectTemporaryStyles(styleId, highlightClass)

    const hash = window.location.hash
    let scrollEndTimeoutId: number | undefined

    const timeoutId = setTimeout(() => {
      if (hash) {
        const rawId = hash.replace("#", "")
        const elementID = resolveElementId?.(rawId) ?? rawId
        const element = document.getElementById(elementID)
        if (element && isActive && scrollNode) {
          onScrollStart()

          scrollToAndHighlight({
            elementID,
            // ScrollView manages scroll positions as (top, left), not
            // window.scrollTo's (x, y), so scroll the container directly.
            scrollContainer: scrollNode,
            duration: scrollDuration,
            offset,
            autofocus,
            highlightClass
          })

          scrollEndTimeoutId = window.setTimeout(() => {
            onScrollEnd()
          }, scrollDuration + 50)
        }
      }
    }, 250)

    return () => {
      clearTimeout(timeoutId)
      if (scrollEndTimeoutId !== undefined) {
        clearTimeout(scrollEndTimeoutId)
      }
      removeStyles()
    }
  }, [
    autofocus,
    highlightClass,
    isActive,
    offset,
    resolveElementId,
    scrollDuration,
    scrollNode,
    styleId
  ])
}

export default useScrollToAndHighlight
